import { zeroAddress } from 'viem'
import * as z from 'zod'

import { addressSchema } from '../../types'

const nullableNumberSchema = z
  .union([z.number(), z.string(), z.null()])
  .transform((value) => (value === null ? null : Number(value)))
const bigNumberishSchema = z.union([z.number(), z.string()]).transform((value) => String(value))
const nullableBigNumberishSchema = z
  .union([z.number(), z.string(), z.null()])
  .transform((value) => (value === null ? null : String(value)))
const decimalsSchema = z.union([z.number(), z.string(), z.null()]).transform((value) => Number(value))

const snapshotTokenSchema = z
  .object({
    name: z.string().default('').catch(''),
    symbol: z.string().default('').catch(''),
    address: addressSchema,
    chainId: z.number(),
    decimals: decimalsSchema.catch(18),
    description: z.string().optional().default('').catch(''),
    displayName: z.string().optional().default('').catch(''),
    displaySymbol: z.string().optional().default('').catch(''),
    category: z.string().optional().default('').catch('')
  })
  .passthrough()

const snapshotApySchema = z
  .object({
    net: nullableNumberSchema.optional().catch(null),
    label: z.string().optional().default('').catch(''),
    grossApr: nullableNumberSchema.optional().catch(null),
    weeklyNet: nullableNumberSchema.optional().catch(null),
    monthlyNet: nullableNumberSchema.optional().catch(null),
    inceptionNet: nullableNumberSchema.optional().catch(null),
    pricePerShare: nullableBigNumberishSchema.optional().catch(null),
    weeklyPricePerShare: nullableBigNumberishSchema.optional().catch(null),
    monthlyPricePerShare: nullableBigNumberishSchema.optional().catch(null)
  })
  .nullable()
  .optional()
  .default(null)

const snapshotTvlSchema = z
  .object({
    close: nullableNumberSchema.optional().catch(null),
    label: z.string().optional().default('').catch(''),
    component: z.string().optional().default('').catch('')
  })
  .nullable()
  .optional()
  .default(null)

const snapshotFeesSchema = z
  .object({
    managementFee: nullableNumberSchema.optional().catch(null),
    performanceFee: nullableNumberSchema.optional().catch(null)
  })
  .nullable()
  .optional()
  .default(null)

const snapshotRiskScoreSchema = z
  .object({
    review: nullableNumberSchema.optional().catch(null),
    testing: nullableNumberSchema.optional().catch(null),
    complexity: nullableNumberSchema.optional().catch(null),
    riskExposure: nullableNumberSchema.optional().catch(null),
    protocolIntegration: nullableNumberSchema.optional().catch(null),
    centralizationRisk: nullableNumberSchema.optional().catch(null),
    externalProtocolAudit: nullableNumberSchema.optional().catch(null),
    externalProtocolCentralisation: nullableNumberSchema.optional().catch(null),
    externalProtocolTvl: nullableNumberSchema.optional().catch(null),
    externalProtocolLongevity: nullableNumberSchema.optional().catch(null),
    externalProtocolType: nullableNumberSchema.optional().catch(null),
    comment: z.string().optional().nullable().catch('')
  })
  .partial()

const snapshotRiskSchema = z
  .object({
    riskLevel: nullableNumberSchema.optional().catch(null),
    riskScore: snapshotRiskScoreSchema.optional()
  })
  .nullable()
  .optional()
  .default(null)

const estimatedAprSchema = z.object({
  apr: z
    .union([z.number(), z.string()])
    .transform((value) => Number(value))
    .optional()
    .nullable()
    .catch(null),
  apy: z
    .union([z.number(), z.string()])
    .transform((value) => Number(value))
    .optional()
    .nullable()
    .catch(null),
  type: z.string().optional().default('').catch(''),
  components: z
    .object({
      boost: nullableNumberSchema.nullish(),
      poolAPY: nullableNumberSchema.nullish(),
      boostedAPR: nullableNumberSchema.nullish(),
      baseAPR: nullableNumberSchema.nullish(),
      rewardsAPR: nullableNumberSchema.nullish(),
      rewardsAPY: nullableNumberSchema.nullish(),
      cvxAPR: nullableNumberSchema.nullish(),
      keepCRV: nullableNumberSchema.nullish(),
      keepVelo: nullableNumberSchema.nullish(),
      netAPR: nullableNumberSchema.nullish(),
      netAPY: nullableNumberSchema.nullish(),
      katanaBonusAPY: nullableNumberSchema.nullish(),
      katanaNativeYield: nullableNumberSchema.nullish(),
      katanaAppRewardsAPR: nullableNumberSchema.nullish(),
      katRewardsAPR: nullableNumberSchema.nullish(),
      steerPointsPerDollar: nullableNumberSchema.nullish(),
      fixedRateKatanaRewards: nullableNumberSchema.nullish(),
      FixedRateKatanaRewards: nullableNumberSchema.nullish()
    })
    .partial()
    .optional()
    .default({})
})

const snapshotPerformanceSchema = z
  .object({
    estimated: estimatedAprSchema.optional().catch(undefined),
    oracle: z
      .object({
        apr: nullableNumberSchema.optional().catch(null),
        apy: nullableNumberSchema.optional().catch(null)
      })
      .partial()
      .optional(),
    historical: z
      .object({
        net: nullableNumberSchema.optional().catch(null),
        weeklyNet: nullableNumberSchema.optional().catch(null),
        monthlyNet: nullableNumberSchema.optional().catch(null),
        inceptionNet: nullableNumberSchema.optional().catch(null)
      })
      .partial()
      .optional()
  })
  .optional()
  .default({})

const snapshotDebtSchema = z
  .object({
    strategy: addressSchema,
    currentDebt: bigNumberishSchema.optional(),
    maxDebt: bigNumberishSchema.optional().default('0').catch('0'),
    totalDebt: bigNumberishSchema.optional(),
    totalGain: bigNumberishSchema.optional(),
    totalLoss: bigNumberishSchema.optional(),
    performanceFee: nullableNumberSchema.optional().catch(null),
    lastReport: nullableNumberSchema.optional().catch(null),
    debtRatio: nullableNumberSchema.optional().catch(null),
    targetDebtRatio: nullableNumberSchema.optional().catch(null),
    maxDebtRatio: nullableNumberSchema.optional().catch(null),
    currentDebtUsd: nullableNumberSchema.optional().catch(null),
    maxDebtUsd: nullableNumberSchema.optional().catch(null)
  })
  .passthrough()

const snapshotCompositionSchema = z
  .object({
    address: addressSchema.optional(),
    strategy: addressSchema.optional(),
    name: z.string().optional().default('').catch(''),
    status: z.union([z.string(), z.number()]).optional().catch(''),
    debtRatio: nullableNumberSchema.optional().catch(null),
    currentDebt: bigNumberishSchema.optional(),
    totalDebt: bigNumberishSchema.optional(),
    maxDebt: bigNumberishSchema.optional(),
    totalGain: bigNumberishSchema.optional(),
    totalLoss: bigNumberishSchema.optional(),
    performanceFee: nullableNumberSchema.optional().catch(null),
    lastReport: nullableNumberSchema.optional().catch(null),
    latestReportApr: nullableNumberSchema.optional().catch(null),
    performance: z
      .object({
        estimated: estimatedAprSchema.optional().catch(undefined),
        oracle: z
          .object({
            apr: nullableNumberSchema.optional().catch(null),
            apy: nullableNumberSchema.optional().catch(null)
          })
          .optional(),
        historical: z
          .object({
            net: nullableNumberSchema.optional().catch(null),
            weeklyNet: nullableNumberSchema.optional().catch(null),
            monthlyNet: nullableNumberSchema.optional().catch(null),
            inceptionNet: nullableNumberSchema.optional().catch(null)
          })
          .optional()
      })
      .optional(),
    totalDebtUsd: nullableNumberSchema.optional().catch(null),
    totalGainUsd: nullableNumberSchema.optional().catch(null),
    totalLossUsd: nullableNumberSchema.optional().catch(null)
  })
  .passthrough()

const snapshotMigrationSchema = z
  .object({
    target: addressSchema.optional().catch(zeroAddress),
    contract: addressSchema.optional().catch(zeroAddress),
    available: z.boolean().optional().default(false).catch(false)
  })
  .optional()
  .default({ target: zeroAddress, contract: zeroAddress, available: false })

const snapshotMetaSchema = z
  .object({
    kind: z.string().optional().default('').catch(''),
    name: z.string().optional().default('').catch(''),
    type: z.string().optional().default('').catch(''),
    token: snapshotTokenSchema.optional(),
    address: addressSchema.optional(),
    chainId: z.number().optional(),
    category: z.string().optional().default('').catch(''),
    isHidden: z.boolean().optional().default(false).catch(false),
    uiNotice: z.string().optional().default('').catch(''),
    isBoosted: z.boolean().optional().default(false).catch(false),
    isRetired: z.boolean().optional().default(false).catch(false),
    isHighlighted: z.boolean().optional().default(false).catch(false),
    migration: snapshotMigrationSchema.optional(),
    sourceURI: z.string().optional().default('').catch(''),
    description: z.string().optional().default('').catch(''),
    displayName: z.string().optional().default('').catch(''),
    displaySymbol: z.string().optional().default('').catch(''),
    shouldUseV2APR: z.boolean().optional().default(false).catch(false)
  })
  .passthrough()

const snapshotAssetSchema = z
  .object({
    name: z.string().default('').catch(''),
    symbol: z.string().default('').catch(''),
    address: addressSchema,
    chainId: z.number(),
    decimals: decimalsSchema.catch(18)
  })
  .passthrough()

const snapshotStakingRewardSchema = z
  .object({
    address: addressSchema.optional().catch(zeroAddress),
    name: z.string().optional().default('').catch(''),
    symbol: z.string().optional().default('').catch(''),
    decimals: decimalsSchema.optional().catch(18),
    price: nullableNumberSchema.optional().catch(null),
    isFinished: z.boolean().optional().catch(false),
    finishedAt: nullableNumberSchema.optional().catch(null),
    apr: nullableNumberSchema.optional().catch(null),
    perWeek: nullableNumberSchema.optional().catch(null)
  })
  .passthrough()

const snapshotStakingSchema = z
  .object({
    address: addressSchema.optional().catch(zeroAddress),
    available: z.boolean().optional().default(false).catch(false),
    source: z.string().optional().default('').catch(''),
    rewards: z.array(snapshotStakingRewardSchema).optional().default([]).catch([])
  })
  .nullable()
  .optional()

const snapshotYieldSplitterSchema = z
  .object({
    enabled: z.literal(true),
    sourceVaultAddress: addressSchema,
    sourceVaultName: z.string().optional().default('').catch(''),
    sourceVaultSymbol: z.string().optional().default('').catch(''),
    wantVaultAddress: addressSchema,
    wantVaultName: z.string().optional().default('').catch(''),
    wantVaultSymbol: z.string().optional().default('').catch(''),
    depositAssetAddress: addressSchema.optional().catch(zeroAddress),
    depositAssetName: z.string().optional().default('').catch(''),
    depositAssetSymbol: z.string().optional().default('').catch(''),
    rewardTokenAddresses: z.array(addressSchema).optional().default([]).catch([]),
    rewardHandlerAddress: addressSchema.optional().catch(zeroAddress),
    tokenizedStrategyAddress: addressSchema.optional().catch(zeroAddress),
    displayType: z.string().optional().default('Yield Splitter').catch('Yield Splitter'),
    displayKind: z.string().optional().default('Vault-to-Vault').catch('Vault-to-Vault'),
    uiDescription: z.string().optional().default('').catch('')
  })
  .passthrough()

export const kongVaultSnapshotSchema = z
  .object({
    address: addressSchema,
    chainId: z.number(),
    apiVersion: z.string().optional().nullable(),
    inceptTime: nullableNumberSchema.optional().catch(null),
    name: z.string().optional().default('').catch(''),
    symbol: z.string().optional().default('').catch(''),
    decimals: decimalsSchema.optional().catch(18),
    asset: snapshotAssetSchema.optional(),
    totalDebt: bigNumberishSchema.optional(),
    totalAssets: bigNumberishSchema.optional().default('0').catch('0'),
    apy: snapshotApySchema,
    tvl: snapshotTvlSchema,
    fees: snapshotFeesSchema,
    risk: snapshotRiskSchema,
    meta: snapshotMetaSchema.optional(),
    performance: snapshotPerformanceSchema.optional().default({}),
    composition: z.array(snapshotCompositionSchema).optional().default([]),
    debts: z.array(snapshotDebtSchema).optional().default([]),
    strategies: z.array(addressSchema).optional().default([]),
    staking: snapshotStakingSchema,
    inclusion: z.record(z.string(), z.boolean()).optional(),
    yieldSplitter: snapshotYieldSplitterSchema.optional()
  })
  .passthrough()

export type TKongVaultSnapshot = z.infer<typeof kongVaultSnapshotSchema>
export type TKongVaultSnapshotDebt = z.infer<typeof snapshotDebtSchema>
export type TKongVaultSnapshotComposition = z.infer<typeof snapshotCompositionSchema>
export type TKongVaultSnapshotStaking = z.infer<typeof snapshotStakingSchema>
export type TKongVaultSnapshotStakingReward = z.infer<typeof snapshotStakingRewardSchema>
export type TKongVaultSnapshotYieldSplitter = z.infer<typeof snapshotYieldSplitterSchema>
